// IMPORT REACT
import * as React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import "./navbardrawer.scss";

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  IconButton,
  List,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  Badge,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CssBaseline from "@mui/material/CssBaseline";
import { BootstrapTooltip } from "../../../components/Common/BootstrapTooltip";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import { styled, Theme, CSSObject } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import MenuOpenOutlinedIcon from "@mui/icons-material/MenuOpenOutlined";
import HmisLogo from "../../../components/CustomButton/HmisLogo/HmisLogo";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import AutoAwesomeMosaicOutlinedIcon from "@mui/icons-material/AutoAwesomeMosaicOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SettingsIcon from "@mui/icons-material/Settings";
import HelpIcon from "../../../assets/images/helpIcon.png";
import CloseIcon from "@mui/icons-material/Close";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import MedicalInformationOutlinedIcon from "@mui/icons-material/MedicalInformationOutlined";
import PersonIcon from "@mui/icons-material/Person";
import { PreferencesContext } from "../../../PreferenceContext";
import { GetOrganizationList } from "../../../services/OrganizationService";
import { GetBranchList } from "../../../services/BranchService";
import LeaderboardRoundedIcon from "@mui/icons-material/LeaderboardRounded";
import { Context } from "../../../services/models/Context";
import { PAGE_SIZE } from "../../../services/common/const";
import { GetSaveUserOrgBranchSelection } from "../../../services/UserService";
import { AlertType } from "../../../services/models/AlertType";
import FinancialYearList from "../FinancialYearList";
import { format } from "date-fns";
import ArticleIcon from "@mui/icons-material/Article";
import TextSnippetOutlinedIcon from "@mui/icons-material/TextSnippetOutlined";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import ConsumerNotFound from "../../../components/Consumer/ConsumerNotFound";
import LocationList from "../LocationList";
import {
  GetNotificationList,
  UpdateNotificationLog,
} from "../../../services/NotificationService";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  UserAvatar,
  ProfilePopover,
  NotificationMenu,
  LocationSelector,
  MenuItemWrapper,
  CollapsibleMenuItem,
  MobileDrawer,
  MobileBottomNav,
  ColorModeToggle,
} from "./components";
import { GetTeamMemberLocationList } from "../../../services/TeamMemberService";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ScheduleIcon from "@mui/icons-material/Schedule";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import { GetWhatsappModuleConfig } from "../../../services/WhatsappService";

// STYLED COMPONENTS
// =================Navbar Drawar============================
const drawerWidth = 210;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: "0.15s",
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: "0.15s",
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

// =================Navbar Drawar============================

//MENU SCHEMA
interface IMenu {
  master: boolean;
  frontDesk: boolean;
  opd: boolean;
  patient: boolean;
  doctor: boolean;
  accounts: boolean;
  team: boolean;
  crm: boolean;
  new: boolean;
}

// INITIAL DATA FOR MENU
const menu: IMenu = {
  master: false,
  opd: false,
  patient: false,
  doctor: false,
  accounts: false,
  team: false,
  crm: false,
  frontDesk: false,
  new: false,
};
const parameters: any = {
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  totalCount: "",
  maxAllowedBranches: "",
  searchValue: "",
};

// SCHEMA FOR RESULTDATA
interface ResultData {
  notificationColl: Array<any>;
  totalCount: number;
  unreadCount: number;
}

// DEFAULT VALUES FOR RESULT DATA
const initResultData: ResultData = {
  notificationColl: [],
  totalCount: 0,
  unreadCount: 0,
};

export default function Nav(props: any) {
  //HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();

  const theme = useTheme();
  const smallScreen = useMediaQuery("(max-width:768px)");
  const smd = useMediaQuery(theme.breakpoints.down("sm"));
  const { search, pathname } = useLocation();
  const { userContext, updateUserContext } =
    React.useContext(PreferencesContext);
  const [param, setParam] = React.useState(parameters);
  const lg = useMediaQuery(theme.breakpoints.down("lg"));

  const [openMenu, setOpenMenu] = React.useState<IMenu>(menu);
  const [orgColl, setOrganizationColl] = React.useState<any[]>([]);
  const [branchColl, setBranchColl] = React.useState<any[]>([]);
  const [state, setState] = React.useState({ left: false });
  const [organizationLogo, setOrganizationLogo] = React.useState("");
  const [customPopUp, setCustomPopUp] = React.useState({
    code: "",
    open: false,
  });
  const [notificationAnchorEl, setNotificationAnchorEl] = React.useState(null);
  const [notificationDetail, setNotificationDetail] =
    React.useState<ResultData>(initResultData);
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [waModuleType, setWaModuleType] = React.useState<
    "MANUAL" | "VISTAMED" | "CLINIC_API" | null
  >(null);

  const isSuperAdmin =
    (userContext?.loginDetail?.userRole || "").toString().toLowerCase() ===
    "superadmin";

  const allowedSubscriptionRoutes = React.useMemo(
    () => ["/subscription", "/help", "/help-center"],
    [],
  );

  const shouldShowSubscriptionDialog =
    (userContext?.subscriptionId || 0) <= 0 &&
    !isSuperAdmin &&
    !allowedSubscriptionRoutes.includes(pathname);

  const handleNotificationOpen = React.useCallback((event: any) => {
    setNotificationAnchorEl(event.currentTarget);
  }, []);

  const handleNotificationClose = React.useCallback(() => {
    setNotificationAnchorEl(null);
  }, []);

  const handleMenuOpen = React.useCallback((event: any, item: any) => {
    setSelectedItem(item);
    setMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = React.useCallback(() => {
    setMenuAnchor(null);
    setSelectedItem(null);
  }, []);

  const isNotificationOpen = Boolean(notificationAnchorEl);
  const [hideDesktopExpiryLine, setHideDesktopExpiryLine] =
    React.useState(false);

  const [hideCreditBanner, setHideCreditBanner] = React.useState(false);
  // Getting Seach Paramms for Pagination
  function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let page = urlParams.get("page");
  let encodedQuery = urlParams.get("q");
  let query = "";
  let curPage = 1;

  if (
    page != null &&
    page != "" &&
    page != undefined &&
    encodedQuery != null &&
    encodedQuery != undefined
  ) {
    curPage = +page;
    // Encoded Query
    encodedQuery = encodeURIComponent(encodedQuery);
    // Decode Query
    query = decodeURIComponent(encodedQuery);
  }

  // const unreadCount =
  //   notificationDetail?.notificationColl?.filter((x: any) => !x.isRead)
  //     ?.length || 0;

  // const unreadCount = notificationDetail?.unreadCount || 0;

  const isUserRole = userContext.loginDetail.userRole === "user";

  const filteredUnreadCount = React.useMemo(() => {
    if (!notificationDetail?.notificationColl) return 0;

    return (
      notificationDetail.notificationColl.filter(
        (x: any) =>
          !x.isRead &&
          x.mediaType === "IN_APP" &&
          x.templateFor === "OPD_CHECKOUT",
      )?.length || 0
    );
  }, [notificationDetail?.notificationColl]);

  const unreadCount = isUserRole
    ? filteredUnreadCount
    : notificationDetail?.unreadCount || 0;

  const latestFive = React.useMemo(
    () =>
      notificationDetail?.notificationColl
        ?.slice()
        .sort(
          (a: any, b: any) =>
            new Date(b.createdDate).getTime() -
            new Date(a.createdDate).getTime(),
        )
        .slice(0, 5) || [],
    [notificationDetail?.notificationColl],
  );

  const handleMarkNotification = React.useCallback(
    async (item: any, isRead: boolean) => {
      try {
        await UpdateNotificationLog({
          notificationDetailjson: JSON.stringify([
            {
              notificationUserMappingId: item.notificationUserMappingId,
              isRead,
            },
          ]),
          dataManagedBy: userContext.loginDetail.userId,
        });

        setNotificationDetail((prev: any) => ({
          ...prev,
          notificationColl: prev.notificationColl.map((row: any) =>
            row.notificationUserMappingId === item.notificationUserMappingId
              ? { ...row, isRead }
              : row,
          ),
          unreadCount: isRead
            ? Math.max(0, prev.unreadCount - 1)
            : prev.unreadCount + 1,
        }));
      } catch (err) {
        console.error("Notification read update failed", err);
      }
    },
    [userContext.loginDetail.userId],
  );

  const handleMarkRead = React.useCallback(
    (item: any) => {
      setMenuAnchor(null);
      setSelectedItem(null);
      handleMarkNotification(item, true);
    },
    [handleMarkNotification],
  );

  const handleMarkUnread = React.useCallback(
    (item: any) => {
      setMenuAnchor(null);
      setSelectedItem(null);
      handleMarkNotification(item, false);
    },
    [handleMarkNotification],
  );

  const hostName = window.location.hostname;

  const handleNavigate = React.useCallback(
    (route: string) => {
      navigate(route);
      setOpenMenu(menu);
    },
    [navigate],
  );

  type Anchor = "top" | "left" | "bottom" | "right";

  const getDateRange = React.useCallback(() => {
    const today = format(new Date(), "yyyy-MM-dd");
    return `fd=${today}&ed=${today}`;
  }, []);

  const menuConfig = React.useMemo(() => {
    const dateRange = getDateRange();
    return [
      {
        type: "simple",
        key: "Dashboard",
        route: "/dashboard",
        label: "Dashboard",
        icon: <HomeOutlinedIcon className="menu-icon" />,
      },
      ...(userContext?.loginDetail?.email === "demo@vista-med.com"
        ? [
            {
              type: "simple",
              key: "ClinicalAnalytics",
              route: "/clinical-analytics",
              label: "Analytics",
              icon: <BarChartRoundedIcon className="menu-icon" />,
            },
          ]
        : []),
      ...(userContext?.loginDetail?.email?.toLowerCase?.() ===
      "dental@vista-med.com"
        ? [
            {
              type: "simple",
              key: "DentalAnalytics",
              route: "/dental-analytics",
              label: "Analytics",
              icon: <BarChartRoundedIcon className="menu-icon" />,
            },
          ]
        : []),
      {
        type: "simple",
        key: "Calendar",
        route: "/calendar",
        label: "Calendar",
        icon: <CalendarMonthIcon className="menu-icon" />,
      },
      {
        type: "simple",
        key: "Consumer",
        route: "/consumers",
        label: "Consumer",
        icon: <PersonIcon className="menu-icon" />,
        condition: userContext.loginDetail.userRole === "superadmin",
      },
      {
        type: "collapsible",
        key: "frontDesk",
        label: "Front Desk",
        icon: <MedicalInformationOutlinedIcon />,
        activeRoutes: [
          "/add-appointment",
          "/appointments",
          "/appointmentbook",
          "/add-mr",
        ],
        subItems: [
          { label: "Add Appointments", route: "/add-appointment" },
          {
            label: "Appointments",
            route: `/appointments?tab=waiting&page=1&q=&${dateRange}`,
          },
        ],
      },
      {
        type: "collapsible",
        key: "opd",
        label: "OPD",
        icon: <MedicalServicesOutlinedIcon />,
        activeRoutes: ["/opd-clinical", "/report-certificate"],
        condition: userContext.loginDetail.userRole !== "user",
        subItems: [
          {
            label: "OPD Clinical",
            route: `/opd-clinical?tab=waiting&page=1&${dateRange}&q=`,
          },
          {
            label: "Report Certificates",
            route: "/report-certificate?page=1&q=",
          },
          // { label: "Report Certificates", route: "/report-certificate?type=certificate&page=1&q=" },
          // { label: "Medical Reports", route: "/report-certificate?type=medical-report&page=1&q=" },
        ],
      },
      {
        type: "collapsible",
        key: "patient",
        label: "Patient",
        icon: <AssignmentIndOutlinedIcon />,
        activeRoutes: ["/patient-master", "/patient-history"],
        subItems: [{ label: "Patients", route: "/patient-master?page=1&q=" }],
      },
      {
        type: "collapsible",
        key: "team",
        label: "Team",
        icon: <GroupsIcon />,
        activeRoutes: [
          "/team-member",
          "/users",
          "/roles",
          "/user-group-access",
        ],
        condition: userContext.loginDetail.userRole !== "user",
        subItems: [
          { label: "Team Member", route: "/team-member?tab=&page=1&q=" },
        ],
      },
      {
        type: "collapsible",
        key: "crm",
        label: "CRM",
        icon: <LeaderboardRoundedIcon />,
        activeRoutes: ["/lead", "/distribute"],
        condition: userContext.loginDetail.userRole === "superadmin",
        subItems: [
          { label: "Lead", route: "/lead?page=1&q=" },
          { label: "Distribute", route: "/distribute" },
        ],
      },
      {
        type: "collapsible",
        key: "accounts",
        label: "Accounts",
        icon: <AccountBalanceIcon />,
        activeRoutes: ["/billing", "/payment-received"],
        subItems: [
          { label: "Billing", route: `/billing?page=1&${dateRange}&q=` },
          { label: "Payment Received", route: "/payment-received?page=1&q=" },
        ],
      },
      {
        type: "simple",
        key: "Master",
        route: "/master",
        label: "Master",
        icon: <AutoAwesomeMosaicOutlinedIcon className="menu-icon" />,
        condition: userContext.loginDetail.userRole !== "user",
      },
      {
        type: "simple",
        key: "Template",
        route: "/templates",
        label: "Template",
        icon: <TextSnippetOutlinedIcon className="menu-icon" />,
        condition: userContext.loginDetail.userRole !== "user",
      },
      {
        type: "simple",
        key: "Notification",
        route: "/notification",
        label: "Notification",
        icon: <ChatBubbleOutlineRoundedIcon className="menu-icon" />,
        condition: userContext.loginDetail.userRole !== "user",
      },
      {
        type: "simple",
        key: "Report",
        route: "/report",
        label: "Report",
        icon: <ArticleIcon className="menu-icon" />,
        condition: userContext.loginDetail.userRole !== "user",
      },
      // {
      //   type: "collapsible",
      //   key: "abdm",
      //   label: "ABDM",
      //   icon: <HealthAndSafetyOutlinedIcon />,
      //   activeRoutes: [
      //     "/abha",
      //     "/hfr",
      //     "/hpr",
      //     "/health-records",
      //     "/consent",
      //   ],
      //   subItems: [
      //     { label: "ABHA", route: "/abha" },
      //     { label: "HFR", route: "/hfr" },
      //     { label: "HPR", route: "/hpr" },
      //     { label: "Health Records", route: "/health-records" },
      //     { label: "Consent", route: "/consent" },
      //   ],
      // },
    ];
  }, [userContext.loginDetail.userRole, getDateRange]);

  const routeMap: Record<string, string> = React.useMemo(() => {
    const dateRange = getDateRange();
    return {
      Dashboard: "/dashboard",
      Calendar: "/calendar",
      Master: "/master",
      Consumer: "/consumers",
      "Add Appointments": "/add-appointment",
      Appointments: `/appointments?tab=waiting&page=1&q=&${dateRange}`,
      "Appointment Book": "/appointmentbook",
      "OPD Clinical": `/opd-clinical?tab=waiting&page=1&${dateRange}&q=`,
      "Report Certificates": "/report-certificate?page=1&q=",
      "Medical Reports": "/report-certificate?type=medical-report&page=1&q=",
      Patients: "/patient-master?page=1&q=",
      Doctors: "/doctors",
      "Referral Doctors": "/referral-doctors",
      "Doctors to be Referred": "/doctor-to-be-referred",
      "Visiting Doctors": "/visiting-doctors",
      "Outreach Visits": "/outreach-visits",
      "Doctor Schedule": "/doctor-schedule",
      Billing: `/billing?page=1&${dateRange}&q=`,
      "Payment Received": "/payment-received?page=1&q=",
      "Team Member": "/team-member?tab=&page=1&q=",
      Users: "/users",
      Roles: "/roles",
      "User Group Access": "/user-group-access",
      Lead: "/lead?page=1&q=",
      Distribute: "/distribute",
      Notification: "/notification",
      Report: "/report",
      Template: "/templates",
      ABHA: "/abha",
      HFR: "/hfr",
      HPR: "/hpr",
      "Health Records": "/health-records",
      Consent: "/consent",
    };
  }, [getDateRange]);

  const toggleDrawer = React.useCallback(
    (anchor: any, open: boolean) =>
      (event: React.KeyboardEvent | React.MouseEvent) => {
        if (
          event &&
          event.type === "keydown" &&
          ((event as React.KeyboardEvent).key === "Tab" ||
            (event as React.KeyboardEvent).key === "Shift")
        ) {
          return;
        }

        const page =
          event !== undefined ? (event.target as HTMLElement).innerText : null;
        setOpenMenu({ ...openMenu, new: true });

        if (page && routeMap[page]) {
          navigate(routeMap[page]);
        }

        setState({ ...state, [anchor]: open });
      },
    [navigate, openMenu, routeMap, state],
  );

  const handleDrawerBtn = React.useCallback(() => {
    setOpenMenu((prev) => ({ ...menu, new: !prev.new }));
  }, []);

  const handleMenuClick = React.useCallback((menuKey: keyof IMenu) => {
    setOpenMenu((prev) => ({
      ...menu,
      new: true,
      [menuKey]: !prev[menuKey],
    }));
  }, []);

  const handleDrawerMouseEnter = React.useCallback(() => {
    setOpenMenu((prev) => ({ ...prev, new: true }));
  }, []);

  const handleDrawerMouseLeave = React.useCallback(() => {
    setOpenMenu(menu);
  }, []);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget as HTMLButtonElement);
    },
    [],
  );

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const profileClose = React.useCallback(
    (page: string) => {
      setAnchorEl(null);

      if (page === "My Profile") {
        navigate("/profile");
      } else if (page === "Sign out") {
        updateUserContext(new Context());
        navigate("/");
      }
    },
    [navigate, updateUserContext],
  );

  const handleMobileNavigate = React.useCallback(
    (route: string) => {
      if (lg) setState({ left: false });
      navigate(route);
      setOpenMenu({ ...menu, new: true });
    },
    [lg, navigate],
  );

  function stringToColor(string: string) {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string?.length; i += 1) {
      hash = string?.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 9)) & 0xff;
      color += `00${value?.toString(12)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */
    return color;
  }

  function stringAvatar(name: string | null | undefined) {
    if (!name) {
      return {
        sx: {
          bgcolor: "#ccc", // Default background color when name is null or empty
          width: 35,
          height: 35,
          fontSize: 15,
        },
        children: "?", // Default character when name is null or empty
      };
    }

    const nameParts = name.split(" ");
    const firstInitial = nameParts[0]?.[0]?.toUpperCase() || "";
    const secondInitial =
      nameParts.length > 1 ? nameParts[1]?.[0]?.toUpperCase() || "" : "";

    return {
      sx: {
        bgcolor: stringToColor(name),
        width: 35,
        height: 35,
        fontSize: 15,
      },
      children: `${firstInitial}${secondInitial || ""}`,
    };
  }

  const [hideProfileDiv, setHideProfileDiv] = React.useState(false);

  const getOrganizationList = React.useCallback(async () => {
    try {
      const params = new URLSearchParams({
        consumerId: userContext.consumerId?.toString() || "",
        pageIndex: param.pageIndex?.toString() || "1",
        pageSize: param.pageSize?.toString() || "",
        sortColumn: param.sortColumn || "",
        sortDirection: param.sortDirection || "",
        searchValue: param.searchValue || "",
      });

      const result: any = await GetOrganizationList(params);
      if (result?.data?.data?.organizationColl) {
        const resultData = result.data.data.organizationColl || [];
        setOrganizationColl(resultData);

        const selectedLogo = resultData.find(
          (e: any) => e.organizationId === userContext.organizationId,
        );
        setOrganizationLogo(selectedLogo?.logoServerImage || "");
      }
    } catch (e) {
      console.log("err:", e);
    }
  }, [userContext.consumerId, userContext.organizationId, param]);

  const getBranchList = React.useCallback(async () => {
    try {
      const params = new URLSearchParams({
        consumerId: userContext.consumerId?.toString() || "",
        orgId: userContext.organizationId?.toString() || "",
        pageIndex: param.pageIndex?.toString() || "1",
        pageSize: param.pageSize?.toString() || "",
        sortColumn: param.sortColumn || "",
        sortDirection: param.sortDirection || "",
        searchValue: param.searchValue || "",
      });

      const result: any = await GetBranchList(params);

      if (result?.data?.data?.branchColl) {
        const resultData = result.data.data.branchColl || [];
        setBranchColl(resultData);
      }
    } catch (e) {
      console.log("err:", e);
    }
  }, [
    userContext.consumerId,
    userContext.organizationId,
    param.pageIndex,
    param.pageSize,
    param.sortColumn,
    param.sortDirection,
    param.searchValue,
  ]);

  const getFirstLocation = React.useCallback(
    async (contextSnapshot: typeof userContext) => {
      try {
        const params = new URLSearchParams({
          consumerId: contextSnapshot.consumerId?.toString(),
          organizationId: contextSnapshot.organizationId?.toString(),
          teamMemberId: contextSnapshot.doctorId?.toString(),
          isContainBranch: "true",
          pageIndex: "1",
          pageSize: "100",
          sortColumn: "",
          sortDirection: "",
          searchValue: "",
        });
        const response: any = await GetTeamMemberLocationList(params);
        const locationColl = response?.data?.data?.locationColl || [];
        if (locationColl.length > 0) {
          const first = locationColl[0];
          const saveParams = new URLSearchParams({
            orgId: contextSnapshot.organizationId?.toString(),
            userId: contextSnapshot.loginDetail.userId?.toString(),
            consumerId: contextSnapshot.consumerId?.toString(),
            locationId: first.locationId?.toString(),
          });
          await GetSaveUserOrgBranchSelection(saveParams);
          return {
            locationId: first.locationId,
            locationName: first.locationName,
            locationTypeCode: first.code,
            locationAddress: first.fullAddressWithPostal,
            locationCity: first.city,
          };
        }
      } catch (e) {
        console.error("Auto location fetch failed", e);
      }
      return null;
    },
    [],
  );

  const handleListItemOrganizationClick = React.useCallback(
    async (item: any) => {
      const params = new URLSearchParams({
        orgId: item.organizationId.toString(),
        userId: userContext.loginDetail.userId.toString(),
        consumerId: userContext.consumerId.toString(),
      });

      try {
        const result: any = await GetSaveUserOrgBranchSelection(params);
        const editDetails = result.data.data;

        const updatedContext = {
          ...userContext,
          organizationId: editDetails.orgId,
          organizationName: editDetails.orgName,
          branchId: editDetails.branchId,
          branchName: editDetails.branchName,
          departmentId: editDetails.departmentId,
          departmentName: editDetails.departmentName,
          specialityId: editDetails.specialityId,
          specialityName: editDetails.specialityName,
          financialYearDetail: {
            ...userContext.financialYearDetail,
            financialYear: editDetails.financialYear,
            financialYearStartDate: editDetails.financialYearStartDate,
            financialYearEndDate: editDetails.financialYearEndDate,
          },
          locationName: editDetails?.locationName ?? "",
          locationAddress: editDetails?.locationAddress ?? "",
          locationcity: editDetails?.locationcity ?? "",
          isAlert: true,
          alertMessage: "Sign in to " + editDetails.orgName,
          alertType: AlertType.Success,
        };

        const locationUpdate = await getFirstLocation(updatedContext);
        updateUserContext({ ...updatedContext, ...(locationUpdate ?? {}) });

        if (pathname === "/dashboard") {
          navigate(0);
        } else {
          navigate("/dashboard");
        }
      } catch (e: any) {
        console.error("Organization switch failed:", e);
      }
    },
    [userContext, pathname, navigate, updateUserContext, getFirstLocation],
  );

  const handleListItemBranchClick = React.useCallback(
    async (item: any) => {
      const params = new URLSearchParams({
        orgId: item.orgId.toString(),
        userId: userContext.loginDetail.userId.toString(),
        consumerId: userContext.consumerId.toString(),
        branchId: item.branchId.toString(),
      });

      try {
        const result: any = await GetSaveUserOrgBranchSelection(params);
        const editDetails = result.data.data;

        const updatedContext = {
          ...userContext,
          branchId: editDetails.branchId,
          branchName: editDetails.branchName,
          appointmentUrl: editDetails.appointmentUrl,
          referBookUrl: editDetails.referBookUrl,
          isAlert: true,
          alertMessage: "Branch changed to " + editDetails.branchName,
          alertType: AlertType.Success,
        };

        updateUserContext(updatedContext);

        if (pathname === "/dashboard") {
          navigate(0);
        } else {
          navigate("/dashboard");
        }
      } catch (e: any) {
        // Optional error feedback (same pattern as org switch)
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage:
            typeof e === "string" ? e : e?.message || "Branch switch failed",
          alertType: AlertType.Error,
        });
      }
    },
    [userContext, pathname, navigate, updateUserContext],
  );

  function stringAvatarOrganization(name: string) {
    return {
      sx: {
        bgcolor: stringToColor(name),
        width: 35,
        height: 35,
        fontSize: 15,
      },
      children: `${name?.split(" ")[0][0]?.toUpperCase()}`,
    };
  }

  const handleCloseCustomPopup = React.useCallback(() => {
    setCustomPopUp({ code: "", open: false });
  }, []);

  const getNotificationList = React.useCallback(async () => {
    try {
      const params = new URLSearchParams({
        consumerId: userContext.consumerId?.toString() || "",
        organizationId: userContext.organizationId?.toString() || "",
        branchId: userContext.branchId?.toString() || "",
        pageIndex: curPage?.toString() || "1",
        pageSize: param.pageSize?.toString() || "",
        sortColumn: param.sortColumn || "",
        sortDirection: param.sortDirection || "",
        searchValue: query || "",
      });

      const result: any = await GetNotificationList(params);
      if (result?.data?.data?.notificationColl) {
        setNotificationDetail(result.data.data);
      }
    } catch (e) {
      console.log("err:", e);
    }
  }, [
    userContext.consumerId,
    userContext.organizationId,
    userContext.branchId,
    curPage,
    param,
    query,
  ]);

  const getPlanEndTime = (value: unknown): number => {
    if (!value) return NaN;

    if (value instanceof Date) {
      return value.getTime();
    }

    if (typeof value === "number") {
      return value;
    }

    if (typeof value === "string") {
      const direct = new Date(value).getTime();
      if (Number.isFinite(direct)) {
        return direct;
      }

      const ddmmyyyy = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (ddmmyyyy) {
        const [, dd, mm, yyyy] = ddmmyyyy;
        return new Date(`${yyyy}-${mm}-${dd}T23:59:59`).getTime();
      }
    }

    return NaN;
  };
  const oneDay = 1000 * 60 * 60 * 24;
  const planEndTime = getPlanEndTime(userContext?.planEndDate);
  const daysToPlanExpiry = Number.isFinite(planEndTime)
    ? Math.ceil((planEndTime - Date.now()) / oneDay)
    : null;

  const expiryBannerDismissKey = `hide-expiry-banner-${userContext?.userId || 0}-${userContext?.planEndDate || ""}`;

  const showExpiryIndicator =
    daysToPlanExpiry !== null && daysToPlanExpiry <= 30;

  const showDesktopExpiryIndicator =
    showExpiryIndicator && !lg && !hideDesktopExpiryLine;
  const showMobileExpiryIndicator =
    showExpiryIndicator && lg && !hideDesktopExpiryLine;
  const showExpiryBanner =
    showDesktopExpiryIndicator || showMobileExpiryIndicator;

  const expiryIndicatorHeight = 25;

  const expiryIndicatorColor =
    daysToPlanExpiry !== null && daysToPlanExpiry <= 15
      ? "#d32f2f"
      : daysToPlanExpiry !== null && daysToPlanExpiry <= 30
        ? "#ed6c02"
        : "#90a4ae";

  const canCloseExpiryLine = daysToPlanExpiry !== null && daysToPlanExpiry > 15;

  //---------------------- whatsAppRemainingCredits ------------------------
  const whatsAppRemainingCredits =
    userContext?.whatsAppRemainingCredits ?? null;

  const showCreditIndicator =
    whatsAppRemainingCredits !== null && whatsAppRemainingCredits <= 500;

  const isCreditCritical =
    whatsAppRemainingCredits !== null && whatsAppRemainingCredits <= 50;

  const creditIndicatorColor = isCreditCritical
    ? "#d32f2f" // red — critical
    : "#ed6c02"; // orange/yellow — warning

  const canCloseCreditLine = showCreditIndicator && !isCreditCritical;

  const creditBannerDismissKey = `hide-credit-banner-${userContext?.userId || 0}`;

  React.useEffect(() => {
    try {
      setHideCreditBanner(
        window.sessionStorage.getItem(creditBannerDismissKey) === "1",
      );
    } catch {
      setHideCreditBanner(false);
    }
  }, [creditBannerDismissKey]);

  const handleCloseCreditLine = () => {
    if (isCreditCritical) return;
    setHideCreditBanner(true);
    try {
      window.sessionStorage.setItem(creditBannerDismissKey, "1");
    } catch {
      // ignore storage errors
    }
  };

  // ✅ Module-type eligibility — sirf VISTAMED/CLINIC_API ke liye credit banner relevant hai
  const isCreditBannerEligibleModule =
    waModuleType === "VISTAMED" || waModuleType === "CLINIC_API";

  // ✅ FINAL combined flag — dismiss-state + credit-value + module-type — sab ek jagah
  // Ye SAME flag offset-calculation (topOffsetWithExpiry) aur JSX render dono mein use hoga,
  // taaki reserved-space aur actual-render kabhi mismatch na ho.
  const shouldShowCreditBannerFinal =
    showCreditIndicator &&
    (isCreditCritical || !hideCreditBanner) &&
    isCreditBannerEligibleModule;

  const totalBannerCount =
    (showExpiryBanner ? 1 : 0) + (shouldShowCreditBannerFinal ? 1 : 0);

  const topOffsetWithExpiry = totalBannerCount * expiryIndicatorHeight;

  React.useEffect(() => {
    try {
      setHideDesktopExpiryLine(
        window.sessionStorage.getItem(expiryBannerDismissKey) === "1",
      );
    } catch {
      setHideDesktopExpiryLine(false);
    }
  }, [expiryBannerDismissKey]);

  const handleCloseDesktopExpiryLine = () => {
    setHideDesktopExpiryLine(true);
    try {
      window.sessionStorage.setItem(expiryBannerDismissKey, "1");
    } catch {
      // ignore storage errors
    }
  };

  // ── Fetch WhatsApp module config ──────────────────────────
  const fetchWaModuleConfig = async () => {
    try {
      const res: any = await GetWhatsappModuleConfig({
        branchId: userContext?.branchId,
        consumerId: userContext?.consumerId,
        orgId: userContext?.organizationId,
        userId: userContext?.loginDetail?.userId,
      });
      const configData =
        res?.data?.data ?? (res?.data?.moduleType ? res?.data : null);
      if (configData?.moduleType) {
        setWaModuleType(configData.moduleType);
      } else {
        setWaModuleType("MANUAL");
      }
    } catch (_) {
      setWaModuleType("MANUAL");
    }
  };

  React.useEffect(() => {
    if (userContext.consumerId) {
      getOrganizationList();
    }
  }, [userContext.consumerId, getOrganizationList]);

  React.useEffect(() => {
    if (userContext.organizationId && userContext.consumerId) {
      getBranchList();
    }
    fetchWaModuleConfig();
  }, [userContext.organizationId, getBranchList]);

  React.useEffect(() => {
    if (userContext.branchId) {
      getNotificationList();
    }
  }, [userContext.branchId, getNotificationList]);

  return (
    <Box>
      <Box
        sx={{
          display: lg ? "block" : "flex",
          width: "100%",
          padding: "0px !important",
        }}
      >
        {showExpiryBanner && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              zIndex: 9999,
              height: `${expiryIndicatorHeight}px`,
              width: "100%",
              backgroundColor: expiryIndicatorColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontWeight: 600,
              fontSize: "11px",
              gap: 0.75,
              lineHeight: 1,
            }}
          >
            <WarningAmberIcon
              sx={{
                fontSize: "15px",
                display: "block",
                flexShrink: 0,
              }}
            />
            <Typography
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
                lineHeight: 1,
                fontSize: "11px !important",
                color: "white !important",
                fontWeight: 600,
              }}
            >
              Your account expires in {daysToPlanExpiry} day
              {daysToPlanExpiry !== 1 ? "s" : ""}
            </Typography>
            {canCloseExpiryLine && (
              <IconButton
                size="small"
                aria-label="close expiry banner"
                onClick={handleCloseDesktopExpiryLine}
                sx={{
                  position: "absolute",
                  right: 4,
                  color: "white",
                  p: "2px",
                }}
              >
                <CloseIcon sx={{ fontSize: "15px" }} />
              </IconButton>
            )}
          </Box>
        )}

        {shouldShowCreditBannerFinal && (
          <Box
            sx={{
              position: "fixed",
              top: `${showExpiryBanner ? expiryIndicatorHeight : 0}px`,
              left: 0,
              zIndex: 9999,
              height: `${expiryIndicatorHeight}px`,
              width: "100%",
              backgroundColor: creditIndicatorColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontWeight: 600,
              fontSize: "11px",
              gap: 0.75,
              lineHeight: 1,
            }}
          >
            <Typography
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
                lineHeight: 1,
                fontSize: "11px !important",
                color: "white !important",
                fontWeight: 600,
              }}
            >
              {isCreditCritical
                ? `⚠️ Only ${whatsAppRemainingCredits} WhatsApp credits left — messages may stop sending. Please recharge.`
                : `WhatsApp credits running low: ${whatsAppRemainingCredits} remaining.`}
            </Typography>
            {canCloseCreditLine && (
              <IconButton
                size="small"
                aria-label="close credit banner"
                onClick={handleCloseCreditLine}
                sx={{
                  position: "absolute",
                  right: 4,
                  color: "white",
                  p: "2px",
                }}
              >
                <CloseIcon sx={{ fontSize: "15px" }} />
              </IconButton>
            )}
          </Box>
        )}
        {lg ? (
          <>
            <AppBar
              style={{
                paddingBottom: "0px !important",
                borderBottom: "1px solid rgba(0, 0, 0, 0.04)",
                zIndex: "1000",
                top: `${topOffsetWithExpiry}px`,
              }}
              position="fixed"
              open={openMenu.new}
            >
              <CssBaseline />
              <Box>
                <Toolbar
                  className="new"
                  sx={{
                    backgroundColor: (t) =>
                      t.palette.mode === "dark"
                        ? t.palette.background.paper
                        : "#ffffff",
                    boxShadow: "rgba(20, 46, 110, 0.1) 0px 1px 8px !important",
                    minHeight: "64px !important",
                    maxHeight: "64px !important",
                  }}
                >
                  <IconButton
                    color="primary"
                    aria-label="open drawer"
                    onClick={toggleDrawer("left", !state.left)}
                    edge="start"
                    sx={{ p: { xs: 0.5, sm: 1 } }}
                  >
                    <MenuIcon />
                  </IconButton>
                  <Box
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{ marginRight: "10px", marginLeft: "4px" }}
                    ></div>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: 0.5, sm: 1 },
                      }}
                    >
                      <ColorModeToggle />
                      <UserAvatar
                        firstName={userContext.loginDetail.firstName}
                        lastName={userContext.loginDetail.lastName}
                        organizationLogo={organizationLogo}
                        onClick={handleClick}
                        id={id}
                      />
                    </Box>
                  </Box>
                </Toolbar>
              </Box>
            </AppBar>

            <MobileDrawer
              open={state.left}
              onClose={() => setState({ left: false })}
              onOpen={() => setState({ left: true })}
              openMenu={openMenu}
              pathname={pathname}
              menuConfig={menuConfig}
              onNavigate={handleMobileNavigate}
              onMenuClick={handleMenuClick}
              routeMap={routeMap}
            />
          </>
        ) : (
          <>
            <AppBar
              style={{
                paddingBottom: "0px !important",
                borderBottom: "1px solid rgba(0, 0, 0, 0.04)",
                top: `${topOffsetWithExpiry}px`,
              }}
              position="fixed"
              open={openMenu.new}
            >
              <CssBaseline />
              <Box>
                <Toolbar
                  sx={{
                    backgroundColor: (t) =>
                      t.palette.mode === "dark"
                        ? t.palette.background.paper
                        : hostName === "hcp.vista-med.com"
                          ? "#f1f5f8"
                          : "#e9d69d",
                    boxShadow: "rgba(20, 46, 110, 0.1) 0px 1px 8px !important",
                    minHeight: "64px !important",
                    maxHeight: "64px !important",
                  }}
                >
                  <IconButton
                    className="icon-color"
                    aria-label="open drawer"
                    onClick={handleDrawerBtn}
                    edge="start"
                  >
                    {openMenu.new ? <MenuOpenOutlinedIcon /> : <MenuIcon />}
                  </IconButton>
                  <Box
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        marginRight: "50px",
                        marginLeft: "10px",
                        marginTop: "8px",
                      }}
                    >
                      <HmisLogo className="logo nav-logo" size={38} />
                    </div>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <LocationSelector
                        organizationName={userContext?.organizationName || ""}
                        name={userContext?.locationName}
                        address={userContext?.locationAddress}
                        locationTypeCode={userContext?.locationTypeCode}
                        locationCity={userContext?.locationCity}
                        userRole={userContext.loginDetail.userRole}
                        onClick={() =>
                          setCustomPopUp({ code: "LOCATION", open: true })
                        }
                      />

                      {userContext.loginDetail.userRole !== "user" && (
                        <Box>
                          <Tooltip title="Settings">
                            <IconButton
                              className="icon-color"
                              aria-label=""
                              onClick={() => navigate("/settings")}
                            >
                              <SettingsIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                      {/* {userContext.loginDetail.userRole !== "user" && ( */}
                      <>
                        <Box>
                          <Tooltip title="Notification">
                            <IconButton
                              className="icon-color"
                              aria-label=""
                              onClick={handleNotificationOpen}
                            >
                              <Badge badgeContent={unreadCount} color="error">
                                <NotificationsIcon />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <NotificationMenu
                          anchorEl={notificationAnchorEl}
                          isOpen={isNotificationOpen}
                          onClose={handleNotificationClose}
                          notifications={latestFive}
                          onNotificationClick={(item) => {
                            handleMarkNotification(item, true);
                            navigate({
                              pathname:
                                "/master/refer-book-entry-list/refer-book-entry-details",
                              search: `?q=${encodeURIComponent(edService.encrypt(item?.referBookEntryId || 0) ?? "")}`,
                            });
                          }}
                          onViewAll={() => {
                            navigate("/notification/activity-list");
                            handleNotificationClose();
                          }}
                          onMarkRead={handleMarkRead}
                          onMarkUnread={handleMarkUnread}
                          menuAnchor={menuAnchor}
                          selectedItem={selectedItem}
                          onMenuOpen={handleMenuOpen}
                          onMenuClose={handleMenuClose}
                        />
                      </>
                      {/* )} */}
                      {/* {userContext.loginDetail.userRole !== "user" && (
                        <Box>
                          <Tooltip title="Notification">
                            <IconButton
                              className="icon-color"
                              aria-label=""
                              onClick={() => navigate("/notification-activity-list")}
                            >
                              <NotificationsNoneOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )} */}
                      <Box>
                        <Tooltip title="Help">
                          <IconButton
                            className="icon-color"
                            aria-label=""
                            onClick={() => navigate("/help")}
                          >
                            <img
                              src={HelpIcon}
                              alt="menu icon"
                              height={25}
                              width={25}
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      <ColorModeToggle />

                      <Box>
                        <UserAvatar
                          firstName={userContext.loginDetail.firstName}
                          lastName={userContext.loginDetail.lastName}
                          organizationLogo={organizationLogo}
                          onClick={handleClick}
                          id={id}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Toolbar>
              </Box>
            </AppBar>
            <Drawer
              variant="permanent"
              open={openMenu.new}
              className="drawer-main"
              style={{ transitionDuration: "0.15s" }}
              onMouseEnter={handleDrawerMouseEnter}
              onMouseLeave={handleDrawerMouseLeave}
            >
              <DrawerHeader sx={{ mt: `${topOffsetWithExpiry}px` }} />
              <List
                className="drawer-container"
                sx={{
                  boxShadow: "rgba(20, 46, 110, 0.1) 0px 1px 8px !important",
                  height: "100vh",
                  border: "none",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: "column",
                  }}
                >
                  {menuConfig.map((item: any) => {
                    if (item.condition === false) return null;

                    if (item.type === "simple") {
                      return (
                        <MenuItemWrapper
                          key={item.key}
                          itemKey={item.key}
                          pathname={pathname}
                          route={item.route}
                          label={item.label}
                          icon={item.icon}
                          isOpen={openMenu.new}
                          onClick={() => handleNavigate(item.route)}
                        />
                      );
                    }

                    return (
                      <CollapsibleMenuItem
                        key={item.key}
                        itemKey={item.key}
                        label={item.label}
                        icon={item.icon}
                        isOpen={openMenu.new}
                        isExpanded={openMenu[item.key as keyof IMenu]}
                        activeRoutes={item.activeRoutes}
                        pathname={pathname}
                        subItems={item.subItems}
                        onToggle={() =>
                          handleMenuClick(item.key as keyof IMenu)
                        }
                        onSubItemClick={(route) => navigate(route)}
                      />
                    );
                  })}
                </span>
              </List>
            </Drawer>
          </>
        )}
        <Box
          component="main"
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
            backgroundColor: "background.default",
            width: "100%",
            flexGrow: 1,
            // Bottom nav (OPD/Calendar/etc.) is for non-staff only — avoid empty gap for staff.
            pb: smallScreen && !isUserRole ? "92px" : 0,
          }}
        >
          <DrawerHeader
            id="drwerheader"
            sx={{ mt: `${topOffsetWithExpiry}px` }}
          />

          {/* Main content area */}
          <Box
            sx={{
              flexGrow: 1,
              padding: { xs: "8px", md: "16px" },
            }}
          >
            <ConsumerNotFound>
              <Outlet />
            </ConsumerNotFound>
          </Box>

          {/* Footer always at bottom */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 3,
              pb: 2,
              flexDirection: smallScreen ? "column" : "row",
              backgroundColor: "background.default",
            }}
          >
            <Typography className="font-size-11">
              Copyrights &copy; {new Date().getFullYear()} - Pediaverse
              Solutions Pvt Ltd
            </Typography>
            <Typography className="font-size-11">
              <a
                href="/terms"
                target="_blank"
                className="blue-link"
                style={{ textDecoration: "none" }}
              >
                Terms of Service
              </a>{" "}
              &nbsp;
              <a
                href="/privacy-policy"
                target="_blank"
                className="blue-link"
                style={{ textDecoration: "none" }}
              >
                Privacy Policy
              </a>
            </Typography>
          </Box>
        </Box>
        {/* Staff (userRole=user) ko OPD/Calendar etc. access nahi — mobile bottom bar hide */}
        {smallScreen && !isUserRole && <MobileBottomNav />}
      </Box>

      <ProfilePopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        userContext={userContext}
        organizationList={orgColl}
        branchList={branchColl}
        onOrganizationClick={handleListItemOrganizationClick}
        onBranchClick={handleListItemBranchClick}
        onProfileClick={() => profileClose("My Profile")}
        onSignOut={() => profileClose("Sign out")}
        onLocationClick={() => {
          handleClose();
          setCustomPopUp({ code: "LOCATION", open: true });
        }}
        stringAvatar={stringAvatar}
        stringAvatarOrganization={stringAvatarOrganization}
      />

      <Dialog
        fullScreen={smd}
        open={customPopUp.open && customPopUp.code === "FINANCIAL_YEAR"}
        onClose={handleCloseCustomPopup}
        scroll={"paper"}
        PaperProps={{
          style: {
            width: smd ? "100%" : "400px",
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography className="bold-600 font-size-24">
              Financial Year
            </Typography>
            <BootstrapTooltip title={"Close"} placement="top">
              <IconButton aria-label="close" onClick={handleCloseCustomPopup}>
                <CloseIcon />
              </IconButton>
            </BootstrapTooltip>
          </Box>
          <Box>
            <Typography>Select financial year to start your work</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            {
              <FinancialYearList
                onselect={(item: any) => {
                  handleCloseCustomPopup();
                }}
              />
            }
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        fullScreen={smd}
        open={customPopUp.open && customPopUp.code === "LOCATION"}
        onClose={handleCloseCustomPopup}
        scroll={"paper"}
        PaperProps={{
          style: {
            width: smd ? "100%" : "500px",
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography className="bold-600 font-size-24">Location</Typography>
            <BootstrapTooltip title={"Close"} placement="top">
              <IconButton aria-label="close" onClick={handleCloseCustomPopup}>
                <CloseIcon />
              </IconButton>
            </BootstrapTooltip>
          </Box>
          <Box>
            <Typography>Select Location </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            {
              <LocationList
                onselect={(item: any) => {
                  handleCloseCustomPopup();
                }}
              />
            }
          </Box>
        </DialogContent>
      </Dialog>

      {(userContext?.subscriptionId || 0) <= 0 && !isSuperAdmin && (
        <Dialog
          fullScreen={smd}
          open={shouldShowSubscriptionDialog}
          maxWidth="sm"
          sx={{
            padding: "16px !important",
            top: 64,
            minWidth: "320px",
          }}
        >
          <DialogContent sx={{ width: "100% !important" }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <InfoOutlinedIcon color="primary" />
              <Box>
                <Typography className="font-size-16 bold-600 primary-color">
                  Requires an upgrade
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  Please upgrade your subscription to get access to the system.
                </Typography>
              </Box>
            </Box>

            <Box display={"flex"} justifyContent={"end"} gap={2}>
              <Button
                variant="contained"
                onClick={() => {
                  navigate("/help");
                }}
              >
                Upgrade
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  profileClose("Sign out");
                }}
              >
                Cancel
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
}
